import { useEffect, useState } from "react";
import { AuthData, CallDataRecords, ComponentProps, Domains } from "./type/Data";

import { Button, Select, Table, DatePicker, Space, Tooltip, Typography, Input, Tabs, Flex } from "antd";
import { getCDRbyDomain, getDomainBillable } from "./services/Endpoints";
import type { DatePickerProps, GetProps } from "antd";
import dayjs, { Dayjs, OptionType } from "dayjs";
import { BaseOptionType } from "antd/es/select";
import { DownloadOutlined, SearchOutlined } from "@ant-design/icons";
import { CSVLink } from "react-csv";

type RangePickerProps = GetProps<typeof DatePicker.RangePicker>;
const { Text } = Typography;

const { Option } = Select;
const { RangePicker } = DatePicker;

interface MonthData {
  key: string;
  date: string;
}

interface MonthRecord {
  month: string;
  totalDuration: number;
  toalTimeTalking: number;
  userRecords: UserRecord[];
}

interface UserRecord {
  user: string;
  outboundTotalDuration: number;
  inboundTotalDuration: number;
  onNetTotalDuration: number;
  outboundTotalTalking: number;
  inboundTotalTalking: number;
  onNetTotalTalking: number;
}

interface MonthsReport {
  start_date: string;
  end_date: string;
}

const ReportsByDomain: React.FC<ComponentProps> = ({ username, password, authData, domains, onBack }) => {
  const [columns, setColumns] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isAllReport, setAllReport] = useState<boolean>(false);
  const [isReportByMonth, setReportByMonth] = useState<boolean>(false);

  const [domainNames, setDomainNames] = useState<string[]>(
    Array.from(new Set(domains.map((item) => item.description))),
  );
  const [selectedCustomer, setSelectedCustomer] = useState<string | undefined>(domainNames[0]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | undefined>();
  const [selectedMonths, setSelectedMonths] = useState<MonthData[]>([]);
  const [cdrData, setCdr] = useState<CallDataRecords[]>([]);
  const [selectedDateRangeString, setSelectedDateRangeString] = useState<[string, string]>();
  const [selectedDateRange, setSelectedDateRange] = useState<RangePickerProps["value"] | null>(null);
  const [selectedMonthsReports, setSelectedMonthsForReports] = useState<MonthsReport[]>([]);
  const [columnsReportByMonth, setColumnsReportByMonth] = useState<any[]>([]);
  const [columnsReportByMonthData, setColumnsReportByMonthData] = useState<MonthRecord[]>([]);
  const [expandedMonth, setExpandedMonth] = useState<string | null>(null);
  const [csvData, setCsvData] = useState(null);
  const [fileName, setFilename] = useState<string>("");

  const tooltipData: any = {
    domain: "The domain owning the cdr.",
    territory: "The Territory/Reseller that the domain belongs to.",
    type: "type 0=Outbound, 1=Inbound, 2=Missed, 3=On-net",
    cdr_id: "The unique ID that links back to the full CDR.",
    orig_sub: "The user extension for the orig side of the call. May by null for inbound calls",
    orig_from_uri: "The callerid for the call.",
    orig_from_name: "The callerid name for the call.",
    orig_req_uri: "The dialed number (sip uri) for the call. Should be available both inbound and outbound",
    orig_req_user: "The dialed number just user for the call. Should be available both inbound and outbound",
    by_sub:
      "The user extension for the by side of the call. May by null for calls without a transfer or user feature applied.",
    term_sub: "The user extension for the term side of the call. May by null for outbound calls",
    term_to_uri: "The connected termination device or number in full sip uri format",
    time_start: "Unixtimestamp/epoch for the start time of the call in GMT",
    time_answer: "Unixtimestamp/epoch for the answer time of the call in GMT",
    time_release: "Unixtimestamp/epoch for the end time of the call in GMT",
    duration: "The duration of the call in seconds",
    time_talking: "The duration of the call in seconds that was in talking state.",
  };

  // useEffect(() => {
  //   setGenerateAll(false);
  // }, [selectedDateRangeString, selectedCustomerId]);

  useEffect(() => {
    setLoading(true);
    // console.log("selectedCustomer", selectedCustomer);
    // console.log("selectedDateRangeString", selectedDateRangeString);

    const selectedCustomerIdNew = selectedCustomerId
      ? selectedCustomerId
      : domains.find((obj) => obj.description === selectedCustomer)?.domain;

    // console.log("allReport", isAllReport);
    // console.log("isReportByMonth", isReportByMonth, selectedMonthsReports);
    // console.log("selectedCustomerIdNew && selectedDateRangeString", selectedCustomerIdNew, selectedDateRangeString);
    if (selectedCustomerIdNew && selectedDateRangeString) {
      if (isAllReport) {
        fetchData(selectedCustomerIdNew, selectedDateRangeString); // Call the async function immediately
      }
      if (isReportByMonth && selectedMonthsReports) {
        fetchReportByMonth(selectedCustomerIdNew, selectedMonthsReports);
      }
    }
    // setAllReport(false);
  }, [
    selectedMonths,
    selectedCustomer,
    selectedDateRangeString,
    selectedCustomerId,
    selectedMonthsReports,
    isReportByMonth,
    isAllReport,
  ]);

  const fetchDomainCdrData = async (
    custommerId: string,
    startDate: string,
    endDate: string,
  ): Promise<CallDataRecords[]> => {
    const response = await getCDRbyDomain(custommerId, startDate, endDate, authData);
    return response;
  };

  const fetchData = async (selectedCustomerId: string, selectedDateRangeString: string[]) => {
    try {
      // console.log("fetchData", selectedDateRangeString, selectedCustomerId);

      const cdr: CallDataRecords[] = await fetchDomainCdrData(
        selectedCustomerId,
        selectedDateRangeString[0],
        selectedDateRangeString[1],
      );

      // console.log("cdr", cdr);

      // get call data records
      setCdr(cdr);

      const uniqueCustomers = Array.from(new Set(domains.map((item) => item.description)));
      setDomainNames(uniqueCustomers);

      const filteredKeys = Object.keys(cdr[0]).filter((key) => key !== "domain");

      const dynamicColumns = filteredKeys?.map((i) => ({
        title: (
          <Tooltip title={tooltipData[i]}>
            <span>{i.toUpperCase().replaceAll("_", " ")}</span>
          </Tooltip>
        ),
        dataIndex: i.replaceAll("-", "_"),
        key: i.replaceAll("-", "_"),
        align: "center",
        ...getColumnSearchProps(i.replaceAll("-", "_")),
      }));

      const columns = [
        {
          title: "DOMAIN",
          dataIndex: "domain",
          key: "domain",
          fixed: "left",
          width: 200,
          sticky: true,
        },
        ...dynamicColumns,
      ];

      setColumns(columns);
      setLoading(false);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const fetchReportByMonth = async (selectedCustomerId: string, selectedMonthsReports: MonthsReport[]) => {
    try {
      const reportByDetails: { [key: string]: CallDataRecords[] } = {};
      const reportByDetailsSub: { [key: string]: CallDataRecords[] } = {};
      const reportByMonth: MonthRecord[] = [];

      // console.log("reportMonth",selectedMonthsReports);

      for (const reportMonth of selectedMonthsReports) {
        const userRecordByMonth: UserRecord[] = [];
        // console.log("reportMonth",reportMonth.start_date,
        // reportMonth.end_date );
        const callDataRecords: CallDataRecords[] = await fetchDomainCdrData(
          selectedCustomerId,
          reportMonth.start_date,
          reportMonth.end_date,
        );

        let duration: number = 0;
        let timeTalking: number = 0;
        let inboundTotalDuration: number = 0;
        let outboundTotalDuration: number = 0;
        let onNetTotalDuration: number = 0;
        let inboundTotalTalking: number = 0;
        let outboundTotalTalking: number = 0;
        let onNetTotalTalking: number = 0;
        const monthRecordsMap = new Map();

        callDataRecords.forEach((record) => {
          if ("3" !== (record.type as unknown as string)) {
            duration += parseInt(record.duration as unknown as string);
            timeTalking += parseInt(record.time_talking as unknown as string);
          }

          // const month = dayjs(reportMonth.start_date).month();
          //   const { orig_sub, orig_from_name, duration, time_talking, type } = record;

          //   // If month record doesn't exist, create it
          //   if (!monthRecordsMap.has(month)) {
          //     monthRecordsMap.set(month, {
          //       month: month,
          //       totalDuration: 0,
          //       totalTalkingTime: 0,
          //       userRecords: [],
          //     });
          //   }

          //   const monthRecord = monthRecordsMap.get(month);
          //   monthRecord.totalDuration += parseInt(duration as unknown as string);
          //   monthRecord.toalTimeTalking += parseInt(time_talking as unknown as string);

          //   // If user record doesn't exist, create it
          let name = "(" + record.orig_sub + ") " + record.orig_from_name;

          //update name if inbound
          if ("1" === (record.type as unknown as string)) {
            name = record.term_sub as unknown as string;
          }
          let userRecord = userRecordByMonth.find((userRec: UserRecord) => userRec.user === name);
          if (!userRecord) {
            userRecord = {
              user: name,
              inboundTotalDuration: 0,
              outboundTotalDuration: 0,
              onNetTotalDuration: 0,
              inboundTotalTalking: 0,
              outboundTotalTalking: 0,
              onNetTotalTalking: 0,
            };
            userRecordByMonth.push(userRecord);
          }

          const recorDuration = parseInt(record.duration as unknown as string);
          const recorTalking = parseInt(record.time_talking as unknown as string);

          //   // Update user record totals based on type
          switch (record.type as unknown as string) {
            case "1":
              userRecord.inboundTotalDuration += recorDuration;
              userRecord.inboundTotalTalking += recorTalking;
              inboundTotalDuration += recorDuration;
              break;
            case "0":
              userRecord.outboundTotalDuration += recorDuration;
              userRecord.outboundTotalTalking += recorTalking;
              inboundTotalDuration += recorDuration;

              break;
            case "3":
              userRecord.onNetTotalDuration += recorDuration;
              userRecord.onNetTotalTalking += recorTalking;
              inboundTotalDuration += recorDuration;
              break;
            case "2":
              // userRecord.onNetTotalDuration += parseInt(record.duration as unknown as string)/60;
              // userRecord.onNetTotalTalking += parseInt(record.time_talking as unknown as string)/60;
              inboundTotalDuration += recorDuration;
              break;
            default:
              break;
          }

          //   monthRecordsMap.set(month, monthRecord);
        });
        // console.log("inboundTotalDuration", inboundTotalDuration / 60);
        // {Math.round((total[col.key] / 60) * 100) / 100} mins

        // console.log("userRecordByMonth", userRecordByMonth);

        reportByMonth.push({
          month: dayjs(reportMonth.start_date).format("MMMM"),
          totalDuration: duration,
          toalTimeTalking: timeTalking,
          userRecords: userRecordByMonth,
        });
        // reportByDetails[dayjs(reportMonth.start_date).month()] = callDataRecords;
      }

      const columns = [
        {
          title: "Month",
          dataIndex: "month",
          key: "month",
        },
        {
          title: "Total Duration(mins)",
          dataIndex: "totalDuration",
          key: "totalDuration",
          render: (inboundTotalDuration: number) => `${(inboundTotalDuration / 60).toFixed(2)}`,
        },
        {
          title: "Total Time talking(mins)",
          dataIndex: "toalTimeTalking",
          key: "toalTimeTalking",
          render: (inboundTotalTalking: number) => `${(inboundTotalTalking / 60).toFixed(2)}`,
        },
      ];

      // console.log("setColumnsReportByMonth", columns);
      // console.log("setColumnsReportByMonthData", reportByMonth);
      setColumnsReportByMonth(columns);
      setColumnsReportByMonthData(reportByMonth);
      handleExport(reportByMonth, selectedCustomerId);
      setLoading(false);
    } catch (err) {}
  };

  const handleFilter = (filterType: string) => {
    // setFilterType(filterType);
  };

  const handleClearFilters = () => {
    setSelectedCustomer(undefined);
    setSelectedDateRange(null);
    setSelectedMonths([]);
    setSelectedDateRangeString(undefined);
    setSelectedCustomerId(undefined);
    setReportByMonth(false);
    setAllReport(false);
    setCdr([]);
    setCsvData(null);
    setFilename("");
    setLoading(true);
  };

  const handleGenerateAllReport = () => {
    setAllReport(true);
    setReportByMonth(false);
    setCdr([]);
    setColumnsReportByMonthData([]);
    setCsvData(null);
    setFilename("");
    setLoading(true);
  };
  const handleGenerateReportByMonths = () => {
    setReportByMonth(true);
    setAllReport(false);
    setCdr([]);
    setColumnsReportByMonthData([]);
    setCsvData(null);
    setFilename("");
    setLoading(true);
  };

  const handleDateRangeChange = (dates: RangePickerProps["value"], dateStrings: [string, string]) => {
    setLoading(true);

    if (dates && dates[0] && dates[1]) {
      setSelectedDateRange(dates);
      const startDateYear = dayjs(dateStrings[0]);
      const endDateYear = dayjs(dateStrings[1]);
      const currentDay = dayjs();

      // console.log("dates", dateStrings[0], dateStrings[1]);

      let finalEndtDate = dateStrings[1];

      if (endDateYear.isBefore(currentDay, "month")) {
        finalEndtDate = endDateYear.endOf("month").format("YYYY-MM-DD");
      }

      const finalStartDate = startDateYear.startOf("month").format("YYYY-MM-DD");
      setSelectedDateRangeString([finalStartDate, finalEndtDate]);
      const months: MonthData[] = [];
      let endDate = startDateYear;
      while (endDate.isBefore(endDateYear) || endDate.isSame(endDateYear, "day")) {
        const month = endDate.format("MMMM");
        const year = endDate.format("YYYY");
        months.push({ key: `${month}-${year}`, date: `${month}-${year}` });
        endDate = endDate.add(1, "month");
      }
      setSelectedMonths(months);

      // Initialize an empty array to store results
      const dateRanges = [];

      // Loop through each month between start_date and end_date
      let currentDate = startDateYear.startOf("month");
      while (
        currentDate.isBefore(endDateYear) ||
        currentDate.isSame(endDateYear, "month")
        // && currentDate. isSameOrBefore(currentDay, "month")
      ) {
        const startOfMonth = currentDate.startOf("month");
        let endOfMonth;

        if (currentDate.isSame(currentDay, "month")) {
          endOfMonth = currentDay;
        } else {
          endOfMonth = currentDate.endOf("month");
        }

        dateRanges.push({
          start_date: startOfMonth.format("YYYY-MM-DD"),
          end_date: endOfMonth.format("YYYY-MM-DD"),
        });

        // Move to the next month
        currentDate = currentDate.add(1, "month");
      }

      setSelectedMonthsForReports(dateRanges);
      setColumnsReportByMonthData([]);
      setCdr([]);

      // console.log("dateRanges", dateRanges);
    } else {
      setSelectedMonths([]);
    }
  };

  // column filtering
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const handleSearch = (selectedKeys: string[], confirm: () => void, dataIndex: string) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void, selectedKeys: string[], confirm: () => void, dataIndex: string) => {
    clearFilters();
    setSearchText("");
    handleSearch(selectedKeys, confirm, dataIndex);
  };

  const getColumnSearchProps = (dataIndex: string) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ width: 188, marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}>
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters, selectedKeys, confirm, dataIndex)}
            size="small"
            style={{ width: 90 }}>
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <span
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}>
        <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      </span>
    ),
    onFilter: (value: string, record: any) => record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
  });

  // customer date change handling
  const handleCustomerChange = (value: string, option: BaseOptionType) => {
    setSelectedCustomer(value);
    setSelectedCustomerId(option.key);
    setColumnsReportByMonthData([]);
    setCdr([]);
    setCsvData(null);
    setFilename("");
  };

  const onOk = (value: DatePickerProps["value"] | RangePickerProps["value"]) => {
    console.log("onOk: ", value);
  };

  const disabledDate = (current: Dayjs) => {
    // Disable all dates after today
    return current.isBefore(dayjs()) ? false : true;
  };

  const handleExport = (data: MonthRecord[], selectedCustomerId: string) => {
    let csv: any = [];

    // Header row
    csv.push([
      "Month",
      "Total Duration(mins)",
      "Total Time Talking(mins)",
      "User",
      "Outbound Total Duration(mins)",
      "Inbound Total Duration(mins)",
      "OnNet Total Duration(mins)",
      "Outbound Total Talking(mins)",
      "Inbound Total Talking(mins)",
      "OnNet Total Talking(mins)",
    ]);

    // const data = columnsReportByMonthData;
    // Data rows
    data.forEach((monthRecord) => {
      monthRecord.userRecords.forEach((userRecord) => {
        // let name = "(" + userRecord.orig_sub + ") " + userRecord.orig_from_name;

        // //update name if inbound
        // if ("1" === (userRecord.type as unknown as string)) {
        //   name = userRecord.term_sub as unknown as string;
        // }

        if (
          userRecord.outboundTotalDuration !== 0 ||
          userRecord.inboundTotalDuration !== 0 ||
          userRecord.onNetTotalDuration !== 0
        ) {
          csv.push([
            monthRecord.month,
            (monthRecord.totalDuration / 60).toFixed(2),
            (monthRecord.toalTimeTalking / 60).toFixed(2),
            userRecord.user,
            (userRecord.outboundTotalDuration / 60).toFixed(2),
            (userRecord.inboundTotalDuration / 60).toFixed(2),
            (userRecord.onNetTotalDuration / 60).toFixed(2),
            (userRecord.outboundTotalTalking / 60).toFixed(2),
            (userRecord.inboundTotalTalking / 60).toFixed(2),
            (userRecord.onNetTotalTalking / 60).toFixed(2),
          ]);
        }
      });
    });

    setCsvData(csv);
    setFilename(selectedCustomerId + " monthly reports.csv");
  };

  const scroll = { x: 5000 };

  return (
    <div className="data-table">
      <Flex gap="small" style={{alignItems:"center"}} >
        <a>Customers </a>
        <Select
          defaultValue={(() => {
            return domainNames[0];
          })()}
          value={selectedCustomer}
          onChange={handleCustomerChange}
          style={{ width: "300px" }}>
          {domains.map((domain) => (
            <Option key={domain.domain} value={domain.description}>
              {domain.description}
            </Option>
          ))}
        </Select>
        <a> Date </a>
        <RangePicker
          picker="month"
          format="YYYY-MM-DD"
          onChange={handleDateRangeChange}
          onOk={onOk}
          value={selectedDateRange}
          disabledDate={disabledDate} // Limit date range to current date
        />
        <Button danger onClick={handleClearFilters}>
          Clear Filters
        </Button>
        {/* <Button onClick={onBack}>Back</Button> */}
        {/* <div style={{ paddingTop: 10, paddingLeft: 64 }}> */}
        <Button onClick={handleGenerateAllReport}>Generate all Report</Button>
        <Button onClick={handleGenerateReportByMonths}>Generate Report By Month</Button>
        {/* <Button onClick={handleExport} icon={<DownloadOutlined />}>
        Export as CSV
      </Button> */}
        {csvData && (
          <CSVLink data={csvData} filename={fileName}>
            Download CSV
          </CSVLink>
        )}
      </Flex>
      {/* </div> */}

      <div className="table-container" style={{ paddingTop: 20, paddingBottom: 64 }}>
        {selectedMonthsReports && columnsReportByMonthData && isReportByMonth && (
          <Table
            dataSource={columnsReportByMonthData}
            columns={columnsReportByMonth}
            loading={loading}
            bordered
            expandable={{
              expandedRowRender: (record: MonthRecord) =>
                expandedMonth === record.month && (
                  // <Table
                  //   columns={columnsReportByMonthSub}
                  //   dataSource={record.userRecords}
                  //   // pagination={true}
                  // />
                  <Tabs defaultActiveKey="inbound" tabPosition="top">
                    <Tabs.TabPane tab="Inbound" key="inbound">
                      <Table<UserRecord>
                        dataSource={record.userRecords.filter((user) => user.inboundTotalDuration > 0)}
                        columns={[
                          { title: "User", dataIndex: "user", key: "user" },
                          {
                            title: "Inbound Total Duration (mins)",
                            dataIndex: "inboundTotalDuration",
                            key: "inboundTotalDuration",
                            render: (inboundTotalDuration: number) => `${(inboundTotalDuration / 60).toFixed(2)}`,
                            defaultSortOrder: "descend",
                            sorter: (a, b) => a.inboundTotalDuration - b.inboundTotalDuration,
                          },
                          {
                            title: "Inbound Total Talk Time (mins)",
                            dataIndex: "inboundTotalTalking",
                            key: "inboundTotalTalking",
                            render: (inboundTotalTalking: number) => `${(inboundTotalTalking / 60).toFixed(2)}`,
                            defaultSortOrder: "descend",
                            sorter: (a, b) => a.inboundTotalTalking - b.inboundTotalTalking,
                          },
                        ]}
                        pagination={false}
                      />
                    </Tabs.TabPane>
                    <Tabs.TabPane tab="Outbound" key="outbound">
                      <Table<UserRecord>
                        dataSource={record.userRecords.filter((user) => user.outboundTotalDuration > 0)}
                        columns={[
                          { title: "User", dataIndex: "user", key: "user" },
                          {
                            title: "Outbound Total Duration (mins)ss",
                            dataIndex: "outboundTotalDuration",
                            key: "outboundTotalDuration",
                            render: (inboundTotalDuration: number) => `${(inboundTotalDuration / 60).toFixed(2)}`,
                            defaultSortOrder: "descend",
                            sorter: (a, b) => a.outboundTotalDuration - b.outboundTotalDuration,
                          },
                          {
                            title: "Outbound Total lk Time (mins)",
                            dataIndex: "outboundTotalTalking",
                            key: "outboundTotalTalking",
                            render: (inboundTotalTalking: number) => `${(inboundTotalTalking / 60).toFixed(2)}`,
                            defaultSortOrder: "descend",
                            sorter: (a, b) => a.outboundTotalTalking - b.outboundTotalTalking,
                          },
                        ]}
                        pagination={false}
                      />
                    </Tabs.TabPane>
                    <Tabs.TabPane tab="On Net" key="onnet">
                      <Table<UserRecord>
                        dataSource={record.userRecords.filter((user) => user.onNetTotalDuration > 0)}
                        columns={[
                          { title: "User", dataIndex: "user", key: "user" },
                          {
                            title: "On Net Total Duration (mins)",
                            dataIndex: "onNetTotalDuration",
                            key: "onNetTotalDuration",
                            render: (inboundTotalDuration: number) => `${(inboundTotalDuration / 60).toFixed(2)}`,
                            defaultSortOrder: "descend",
                            sorter: (a, b) => a.onNetTotalDuration - b.onNetTotalDuration,
                          },
                          {
                            title: "On Net Total lk Time (mins)",
                            dataIndex: "onNetTotalTalking",
                            key: "onNetTotalTalking",
                            render: (inboundTotalTalking: number) => `${(inboundTotalTalking / 60).toFixed(2)}`,
                            defaultSortOrder: "descend",
                            sorter: (a, b) => a.onNetTotalTalking - b.onNetTotalTalking,
                          },
                        ]}
                        pagination={false}
                      />
                    </Tabs.TabPane>
                  </Tabs>
                ),
              onExpand: (expanded, record) => {
                setExpandedMonth(expanded ? record.month : null);
              },
              // rowExpandable: (record) => true,
              rowExpandable: (record) => expandedMonth === record.month || true,
            }}
          />
        )}
        {selectedCustomerId && selectedDateRangeString && isAllReport && (
          <Table
            dataSource={cdrData}
            columns={columns}
            bordered
            scroll={scroll}
            loading={loading}
            summary={(pageData) => {
              const columnsToSum = ["duration", "time_talking"];
              const columnIndexMap: any = {};
              if (pageData.length > 0) {
                Object.keys(pageData[0]).forEach((key, index) => {
                  columnIndexMap[key as keyof CallDataRecords] = index;
                });
              }

              const total: any = {};
              columnsToSum.forEach((column) => {
                total[column] = pageData.reduce((acc, row: any) => acc + parseFloat(row[column] as string), 0);
              });

              let totalBorrow = 0;

              pageData.forEach(({ duration }) => {
                totalBorrow += parseInt(duration as unknown as string);
              });
              return (
                <Table.Summary.Row style={{ background: "rgb(227 225 225)" }}>
                  {columns.map((col) => (
                    <Table.Summary.Cell align="center" key={col.title} index={col.title}>
                      {(() => {
                        if (columnsToSum.includes(col.key)) {
                          return (
                            <Text style={{ textAlign: "center" }} type="danger">
                              {Math.round((total[col.key] / 60) * 100) / 100} mins
                            </Text>
                          );
                        } else if (columns.indexOf(col) === 0) {
                          return "Summary";
                        }
                      })()}
                    </Table.Summary.Cell>
                  ))}
                </Table.Summary.Row>
              );
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ReportsByDomain;
